import type { ApiClient } from './client';

/**
 * The product list, as `GET /v1/products` returns it.
 *
 * Read off `veri-plus-backend`: `routes/v1/product.routes.ts` validates
 * `listProductsQuery` (`cursor`, `limit` 1 to 100, `status`) and
 * `productController.list` answers `{ products, nextCursor }`. The product is
 * `product.model.ts` through its `toJSON`, so `_id` arrives as `id`.
 *
 * The board draws no pager, so the screen loads every page, see D7.
 */

export type ProductStatus = 'active' | 'archived';

export interface Product {
  id: string;
  name: string;
  category: string;
  description?: string;
  imageUrl?: string;
  status: ProductStatus;
  createdAt: string;
  updatedAt: string;
}

interface ProductPage {
  products: Product[];
  nextCursor: string | null;
}

/** The API's own ceiling, `paginationQuery` in `validation/common.ts`. */
export const pageLimit = 100;

/** Follows `nextCursor` until it runs out. A cursor that repeats ends the walk. */
export async function listAllProducts(api: ApiClient, signal?: AbortSignal): Promise<Product[]> {
  const all: Product[] = [];
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const query: string = cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`;
    const page: ProductPage = await api.get<ProductPage>(
      `/v1/products?limit=${pageLimit}${query}`,
      signal ? { signal } : {},
    );
    all.push(...page.products);
    cursor = page.nextCursor ?? null;
    if (cursor !== null) {
      if (seen.has(cursor)) break;
      seen.add(cursor);
    }
  } while (cursor !== null);
  return all;
}

/** One entry of `GET /v1/organization/readiness`, `organizationService.readiness`. */
export interface ProductReadiness {
  productId: string;
  regulated: boolean;
  issues: { field: string; message: string }[];
}

export async function getProductReadiness(
  api: ApiClient,
  signal?: AbortSignal,
): Promise<ProductReadiness[]> {
  const body = await api.get<{ products: ProductReadiness[] }>(
    '/v1/organization/readiness',
    signal ? { signal } : {},
  );
  return body.products;
}

/**
 * The category labels, `productCategoryMeta` in `product.model.ts`. They are
 * the API's names for its own enum, not design.
 */
export const categoryLabels: Record<string, string> = {
  pharmaceutical: 'Pharmaceutical',
  medical_device: 'Medical device',
  agrochemical: 'Agrochemical',
  infant_formula: 'Infant formula',
  beverage: 'Beverage',
  food: 'Food',
  cosmetic: 'Cosmetic',
  personal_care: 'Personal care',
  electronics: 'Electronics',
  apparel: 'Apparel',
  other: 'Other',
};

export const categoryLabel = (category: string) => categoryLabels[category] ?? category;

/** The four pills `PortalProducts` draws. */
export type ProductPill = 'active' | 'needs_photo' | 'needs_batch_details' | 'archived';

/**
 * Which pill a row shows, D7.
 *
 * Readiness covers active products only. A missing photo outranks the batch
 * details, and every regulated product owes batch details on each batch, so
 * an active regulated product with no photo gap reads "Needs batch details".
 */
export function pillFor(product: Product, readiness: ProductReadiness | undefined): ProductPill {
  if (product.status === 'archived') return 'archived';
  if (!readiness) return 'active';
  if (readiness.issues.some((issue) => issue.field === 'imageUrl')) return 'needs_photo';
  if (readiness.regulated) return 'needs_batch_details';
  return 'active';
}

/**
 * What `POST /v1/products` takes, `createProductSchema` in
 * `validation/product.validation.ts`. `category` is left out when none is
 * chosen, so the API's own "Required" lands under the category card.
 */
export interface ProductFields {
  name: string;
  category?: string;
  description: string;
}

/** `productController.create` answers 201 `{ product }`. Org admins only. */
export async function createProduct(api: ApiClient, fields: ProductFields): Promise<Product> {
  const body = await api.post<{ product: Product }>('/v1/products', fields);
  return body.product;
}

/** `PATCH /v1/products/:productId`, `updateProductSchema`. */
export async function updateProduct(
  api: ApiClient,
  productId: string,
  fields: ProductFields,
): Promise<Product> {
  const body = await api.patch<{ product: Product }>(
    `/v1/products/${encodeURIComponent(productId)}`,
    fields,
  );
  return body.product;
}

/**
 * `POST /v1/products/:productId/image`, one multipart file in an `image` field
 * (`uploadProductImage` in `middlewares/uploadPhoto.ts`). The API reads the
 * magic bytes, not the file's type, and answers `{ imageUrl }`.
 */
export async function uploadProductImage(
  api: ApiClient,
  productId: string,
  file: Blob,
): Promise<string> {
  const form = new FormData();
  form.append('image', file);
  const body = await api.post<{ imageUrl: string }>(
    `/v1/products/${encodeURIComponent(productId)}/image`,
    form,
  );
  return body.imageUrl;
}

/**
 * The categories `PortalProductForm` draws, in its order. It draws ten; the
 * API's eleventh, `other`, is not drawn and not offered, D10.
 */
export const formCategories = [
  'beverage',
  'food',
  'pharmaceutical',
  'medical_device',
  'agrochemical',
  'infant_formula',
  'cosmetic',
  'personal_care',
  'electronics',
  'apparel',
] as const;

/** `regulated: true` in `productCategoryMeta`. The board pills the same four. */
export const regulatedCategories: ReadonlySet<string> = new Set([
  'pharmaceutical',
  'medical_device',
  'agrochemical',
  'infant_formula',
]);
